import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import IPagination from 'src/models/pagination';
import moment from 'moment';
import { ClickTrackingType } from './model';
import { ClickTrackingReportPageResult, FiltersResult } from 'src/api_v2/user-click-tracking/types';

export interface ClickTrackingState extends IPagination {
  total: number;
  data: ClickTrackingType[];
  mirrorTypeFilter: string;
  contentFilter: string;
  shopNameFilter: string;
  from: number;
  to: number;
  loading: boolean;
  error: boolean;
  contents: { [key: string]: string };
  gameNameEngList?: { [key: string]: string };
  gameNameJpnList?: { [key: string]: string };
  mirrorTypes: string[];
  mirrorIds: string[];
  shopNames: string[];
  exporting: boolean;
  selectedMirrors: string[]
}

export const initialState: ClickTrackingState = {
  data: [],
  total: 0,
  page: 1,
  pageSize: 50,
  sort: 'datetime|desc',
  search: '',
  loading: false,
  error: false,
  mirrorTypeFilter: '',
  contentFilter: '',
  shopNameFilter: '',
  from: moment().subtract(30, 'days').startOf('day').utc().valueOf(),
  to: moment().endOf('day').utc().valueOf(),
  contents: {},
  gameNameEngList: {},
  gameNameJpnList: {},
  mirrorTypes: [],
  mirrorIds: [],
  shopNames: [],
  exporting: false,
  selectedMirrors: []
};

const clickTrackingSlice = createSlice({
  name: 'clickTrackingV2',
  initialState,
  reducers: {
    resetState: (state: ClickTrackingState) => ({
      ...initialState,
      mirrorTypes: state.mirrorTypes,
      contents: state.contents
    }),
    fetchClickTracking(state: ClickTrackingState) {
      state.loading = true;
    },
    fetchTotalClickTracking(state: ClickTrackingState) {
      state.loading = true;
    },
    clickTrackingFetched(
      state: ClickTrackingState,
      action: PayloadAction<ClickTrackingReportPageResult>
    ) {
      state.loading = false;
      state.error = false;
      state.data = action.payload.data;
      state.total = action.payload.totalCount;
    },
    clickTrackingFetchedError(state: ClickTrackingState) {
      state.loading = false;
      state.error = true;
    },
    fetchFilters(state: ClickTrackingState) {},
    filtersFetched(
      state: ClickTrackingState,
      action: PayloadAction<FiltersResult>
    ) {
      state.contents = action.payload.mirrorContent;
      state.gameNameEngList = action.payload.gameNameEngList;
      state.gameNameJpnList = action.payload.gameNameJpnList;
      state.mirrorTypes = action.payload.mirrorTypes;
      state.mirrorIds = action.payload.mirrorIds;
      state.shopNames = action.payload.shopNames;
    },
    fetchFiltersError(state: ClickTrackingState) {
      state.error = true;
    },
    setSort(state: ClickTrackingState, action: PayloadAction<string>) {
      state.sort = action.payload;
    },
    setSearch(state: ClickTrackingState, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setPageSize(state: ClickTrackingState, action: PayloadAction<number>) {
      state.pageSize = action.payload;
    },
    setCurrentPage(state: ClickTrackingState, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setMirrorTypeFilter(
      state: ClickTrackingState,
      action: PayloadAction<string>
    ) {
      state.mirrorTypeFilter = action.payload;
    },
    setContentFilter(state: ClickTrackingState, action: PayloadAction<string>) {
      state.contentFilter = action.payload;
    },
    setFrom(state: ClickTrackingState, action: PayloadAction<number>) {
      state.from = action.payload;
      if (state.to < action.payload) {
        state.to = moment(action.payload)
          .add(1, 'days')
          .endOf('days')
          .utc()
          .valueOf();
      }
    },
    setTo(state: ClickTrackingState, action: PayloadAction<number>) {
      state.to = action.payload;
    },
    setFullPageSize(state: ClickTrackingState) {
      state.page = 1;
      state.pageSize = state.total;
    },
    exportCSV(state: ClickTrackingState) {
      state.exporting = true;
    },
    csvExported(state: ClickTrackingState) {
      state.exporting = false;
    },
    csvExportedFail(state: ClickTrackingState) {
      state.exporting = false;
    },
    setShopNameFilter(state: ClickTrackingState, action: PayloadAction<string>) {
      state.shopNameFilter = action.payload
    },
    setSelectedMirrors(state: ClickTrackingState, action: PayloadAction<string[]>) {
      state.selectedMirrors = action.payload
    }
  }
});

export default clickTrackingSlice.reducer;
export const clickTrackingActions = clickTrackingSlice.actions;
